<template>
    <PhotosListControlsComponent
        :folder="folder"
        :selected="selected"
        @removeSelected="removeSelected"
    />

    <section v-if="isLoadingPhotos" class="pb-16 relative mt-4">
        <ul class="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-8">
            <li class="space-y-2" v-for="n in 8" :key="`skeleton-${n}`">
                <div class="skeleton rounded-lg w-full aspect-10/7"></div>
                <div class="skeleton rounded h-4 w-3/4"></div>
                <div class="flex items-center justify-between">
                    <div class="skeleton rounded h-4 w-1/4"></div>
                </div>
            </li>
        </ul>
    </section>

    <section v-if="!isLoadingPhotos" :class="{'pb-16 relative': true, 'mt-4': selected.length === 0}" aria-labelledby="gallery-heading" ref="photosContainer">
        <draggable
            v-model="sortedPhotos"
            @start="drag=true"
            @end="drag=false"
            item-key="id"
            tag="ul"
            role="list"
            :class="{'grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-8': true}"
            :disabled="isSelecting"
        >
            <template #item="{element, index}">
                <li
                    class="relative group cursor-pointer photo-list-item"
                    @mouseenter="onVideoMouseEnter($event)"
                    @mouseleave="onVideoMouseLeave($event)"
                    :ref="`photo-${index}`">
                    <div :class="[
                        isSelectedPhoto(element)
                        ? 'ring-2 ring-royal-blue-500 ring-offset-2'
                        : null,

                        'bg-gray-200 group block w-full overflow-hidden rounded-lg bg-gray-100 relative']">
                        <div v-if="element.is_compressing" class="pointer-events-none w-full aspect-10/7 bg-gray-200 flex flex-col items-center justify-center gap-2 text-gray-500">
                            <svg class="w-6 h-6 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                            <span class="text-sm">{{ $t('files.file_is_compressing') }}</span>
                        </div>

                        <video v-if="!element.is_compressing && element.is_video && element.compressed_video_url" class="pointer-events-none w-full aspect-10/7 object-cover transition-transform duration-700 group-hover:scale-105" preload="metadata" playsinline muted :poster="element.preview_url" loop>
                            <source :src="element.compressed_video_url" type="video/mp4">
                            Your browser does not support the video tag.
                        </video>
                        <img v-if="!element.is_compressing && (!element.is_video || element.compressed_video_url === null)" :src="element.preview_mini_url" :alt="element.original_filename"
                             @error="(e) => e.target.src = require(`@/assets/images/img-error.jpg`)"
                             :class="[
                                 isSelectedPhoto(element)
                                 ? ''
                                 : 'group-hover:opacity-80',

                                 'pointer-events-none w-full aspect-10/7 object-cover transition-transform duration-700 group-hover:scale-105']" />

                        <div 
                            class="absolute top-0 left-0 right-0 bottom-0" 
                            @click.prevent="select(element, index, $event)"
                            @contextmenu.prevent="showContextMenuForPhoto(element, $event)"
                        ></div>

                        <button
                            v-if="!isTrashedFolder"
                            @click="setCover(element)"
                            :class="{'absolute top-2.5 left-2.5 text-white cursor-pointer transition-opacity duration-300': true, 'group-hover:opacity-90 opacity-0 hover:opacity-70': !element.is_cover, 'opacity-90': element.is_cover}">
                            <StarIcon v-if="!element.is_cover" class="shrink-0 size-5" />
                            <StarFilledIcon v-if="element.is_cover" class="shrink-0 size-5" />
                        </button>

                        <div v-if="element.is_video" class="absolute bottom-2.5 left-2.5 text-white">
                            <VideoCameraIcon class="shrink-0 size-5" />
                        </div>

                        <div v-if="element.is_hidden_by_client" class="absolute bottom-2.5 right-2.5 text-white/70">
                            <EyeSlashIcon class="shrink-0 size-5" />
                        </div>

                        <div class="absolute top-2 right-2 flex gap-1.5">
                            <button
                                v-if="isTrashedFolder"
                                class="text-white bg-royal-blue-500 p-1 rounded-full hover:bg-royal-blue-600 focus:outline-none cursor-pointer transition-opacity duration-300"
                                @click.prevent="recoverItem(element)"
                                :disabled="isRecoveringDeletedItem"
                            >
                                <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                                </svg>
                            </button>

                            <button
                                :class="{'text-white bg-red-600 p-1 rounded-full hover:bg-red-700 focus:outline-none cursor-pointer transition-opacity duration-300': true, 'group-hover:opacity-90 opacity-0': !isTrashedFolder}"
                                @click.prevent="deleteItem(element)"
                                :disabled="isDeletingPhoto"
                            >
                                <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M14 3a.702.702 0 0 1-.037.225l-1.684 10.104A2 2 0 0 1 10.305 15H5.694a2 2 0 0 1-1.973-1.671L2.037 3.225A.703.703 0 0 1 2 3c0-1.105 2.686-2 6-2s6 .895 6 2zM3.215 4.207l1.493 8.957a1 1 0 0 0 .986.836h4.612a1 1 0 0 0 .986-.836l1.493-8.957C11.69 4.689 9.954 5 8 5c-1.954 0-3.69-.311-4.785-.793z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <p class="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900" v-if="isTrashedFolder">{{ element.deleted_at }}</p>
                    <p class="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900" v-if="!isTrashedFolder">{{ element.original_filename }}</p>
                    <p class="pointer-events-none block text-sm font-medium text-gray-500 flex items-center">
                        <span class="mr-auto">{{ formatStorage(element.filesize, $t) }}</span>
                        <span
                            v-for="(folder, folderIndex) in element.folders"
                            :key="`folder-photo-label-${element.id}-${folder.id}-${folderIndex}`"
                            class="ml-1"
                        >
                            <svg class="size-1.5" :style="{fill: folder.color}" viewBox="0 0 6 6" aria-hidden="true">
                              <circle cx="3" cy="3" r="3" />
                            </svg>
                        </span>
                    </p>
                </li>
            </template>
        </draggable>

        <div class="
            w-full flex items-center justify-center text-center mx-auto my-4 mt-8
            text-gray-600
            " v-if="photos.length === 0">
            <span class="text-muted" v-if="!isTrashedFolder && folder">
                {{ $t('files.empty.folders_1') }} <br />{{ $t('files.empty.folders_2') }}
            </span>
            <span class="text-muted" v-if="!folder && !isTrashedFolder">
                {{ $t('files.empty.files') }}
            </span>
            <span class="text-muted" v-if="isTrashedFolder">
                {{ $t('files.empty.trashed') }}
            </span>
        </div>

        <div class="selection-box" v-if="isSelecting" :style="selectionBoxStyle"></div>
    </section>

    <DeletePhotosConfirmationModalComponent
        :open="isDeletingConfirmation !== null"
        @close="isDeletingConfirmation = null"
        @confirmAction="deleteItem(isDeletingConfirmation)"
        :title="$t('files.delete_confirmation_title')"
        :message="isTrashedFolder ? $t('files.delete_confirmation_message') : $t('files.delete_confirmation_trash')"
        :buttonText="$t('files.delete_confirmation_button')"
        :isLoading="isDeletingPhoto"
    />

    <ContextMenuComponent
        :photo="contextMenuPhoto"
        :folders="allFolders"
        :is-visible="showContextMenu"
        :position="contextMenuPosition"
        :is-trashed-folder="isTrashedFolder"
        @close="closeContextMenu"
        @delete="handleContextDelete"
        @recover="handleContextRecover"
        @add-to-scene="handleAddToScene"
        @make-cover="handleMakeCover"
    />

</template>

<script lang="ts">
import { defineComponent } from "vue";
import draggable from "vuedraggable";
import { Photo } from "@/types/PhotoType";
import { mapActions, mapGetters } from "vuex";
import PhotosListControlsComponent from "@/components/Galleries/PhotosListControlsComponent.vue";
import {formatStorage} from "@/_helpers/formatters";
import DeletePhotosConfirmationModalComponent from "@/components/Galleries/Modals/DeletePhotosConfirmationModalComponent.vue";
import ContextMenuComponent from "@/components/Galleries/ContextMenuComponent.vue";
import {StarIcon} from "@heroicons/vue/24/outline";
import {VideoCameraIcon, StarIcon as StarFilledIcon, EyeSlashIcon} from "@heroicons/vue/20/solid";

export default defineComponent({
    name: "PhotosListComponent",

    components: {
        DeletePhotosConfirmationModalComponent,
        PhotosListControlsComponent,
        ContextMenuComponent,
        draggable,
        VideoCameraIcon,
        StarIcon,
        StarFilledIcon,
        EyeSlashIcon,
    },

    props: {
        layout: Number,
        folder: Object,
        smallGrid: Boolean,
    },

    data() {
        return {
            selected: [] as Photo[],
            lastSelectedIndex: null as number | null,
            drag: false,
            isSelecting: false,
            selectionStart: { x: 0, y: 0 },
            selectionEnd: { x: 0, y: 0 },
            isSelectMode: false,

            isDeletingConfirmation: null as Photo | null,

            // Context menu
            showContextMenu: false,
            contextMenuPhoto: null as Photo | null,
            contextMenuPosition: { x: 0, y: 0 },
        };
    },

    computed: {
        ...mapGetters({
            photos: "photos/photos",
            gallery: "galleries/gallery",
            folders: "galleries/folders",
            isLoadingPhotos: "photos/isLoadingList",
            isDeletingPhoto: "photos/isDeletingItem",
            isRecoveringDeletedItem: "photos/isRecoveringDeletedItem",
        }),

        isTrashedFolder(): boolean {
            return (this.folder != null) && this.folder?.slug == 'trash';
        },

        availableFolders() {
            const folders = this.folders as any[];
            return folders.filter((folder: any) => folder.slug !== 'trash');
        },

        allFolders() {
            const folders = this.folders as any[];
            return folders.filter((folder: any) => folder.slug !== 'trash');
        },

        sortedPhotos: {
            get(): Photo[] {
                return this.photos;
            },
            set(value: Photo[]) {
                this.sortPhotos({
                    photos: value,
                    gallery: this.gallery.slug,
                    type: "manual",
                    folder: this.folder?.slug,
                });

                this.$emit("photo-order-manually");
            },
        },

        gridCol(): Number {
            if(this.smallGrid) {
                return 2;
            }

            switch (this.layout) {
                case 2:
                    return 6;
                case 4:
                    return 3;
                case 5:
                    return 2;
                default:
                    return 4;
            }
        },

        selectionBoxStyle(): object {
            const width = Math.abs(this.selectionEnd.x - this.selectionStart.x);
            const height = Math.abs(this.selectionEnd.y - this.selectionStart.y);
            const left = Math.min(this.selectionStart.x, this.selectionEnd.x) + "px";
            const top = Math.min(this.selectionStart.y, this.selectionEnd.y) + "px";

            return {
                width: width + "px",
                height: height + "px",
                left,
                top,
                position: "absolute",
                border: "2px dashed #3498db",
                backgroundColor: "rgba(52, 152, 219, 0.2)",
                zIndex: 100,
            };
        },
    },

    methods: {
        formatStorage,
        ...mapActions({
            deletePhoto: "photos/deletePhoto",
            deleteTrashedPhoto: "photos/deleteTrashedPhoto",
            recoverDeletedPhoto: "photos/recoverDeletedPhoto",
            sortPhotos: "photos/sortPhotos",
            coverPhoto: "photos/coverPhoto",
            moveToFolder: "photos/moveToFolder",

            loadGallery: "galleries/loadGallery",

            successAlert: "alerts/success",
            errorAlert: "alerts/error",
        }),

        onKeyDown(event: KeyboardEvent) {
            if (event.ctrlKey || event.metaKey) {
                this.isSelectMode = true;
            }

            // Handle CMD+A/CTRL+A for select all
            if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
                event.preventDefault();
                this.selectAllPhotos();
                return;
            }

            if(event.key === "Escape") {
                this.isSelectMode = false;
                this.selected = [];
                this.closeContextMenu();
            }
        },

        onKeyUp(event: KeyboardEvent) {
            if (!event.ctrlKey && !event.metaKey) {
                this.isSelectMode = false;
            }
        },

        startSelection(event: MouseEvent) {
            if (!this.isSelectMode) return;

            const container = this.$refs.photosContainer as HTMLElement;
            const containerRect = container.getBoundingClientRect();

            this.isSelecting = true;
            this.selectionStart = {
                x: event.clientX - containerRect.left,
                y: event.clientY - containerRect.top
            };
            this.selectionEnd = { ...this.selectionStart };

            window.addEventListener("mousemove", this.updateSelection);
            window.addEventListener("mouseup", this.endSelection);
            event.preventDefault();
        },

        updateSelection(event: MouseEvent) {
            const container = this.$refs.photosContainer as HTMLElement;
            const containerRect = container.getBoundingClientRect();

            this.selectionEnd = {
                x: event.clientX - containerRect.left,
                y: event.clientY - containerRect.top
            };

            this.updateSelectedPhotos();
        },

        updateSelectedPhotos() {
            const container = this.$refs.photosContainer as HTMLElement;
            const containerRect = container.getBoundingClientRect();

            const selectionRect = {
                left: Math.min(this.selectionStart.x, this.selectionEnd.x),
                top: Math.min(this.selectionStart.y, this.selectionEnd.y),
                right: Math.max(this.selectionStart.x, this.selectionEnd.x),
                bottom: Math.max(this.selectionStart.y, this.selectionEnd.y),
            };

            const photoElements = container.querySelectorAll('.photo-list-item');

            this.selected = this.sortedPhotos.filter((photo, index) => {
                const photoElement = photoElements[index] as HTMLElement;
                if (!photoElement) return false;

                const photoRect = photoElement.getBoundingClientRect();

                const relativePhotoRect = {
                    left: photoRect.left - containerRect.left,
                    right: photoRect.right - containerRect.left,
                    top: photoRect.top - containerRect.top,
                    bottom: photoRect.bottom - containerRect.top,
                };

                return !(
                    selectionRect.right < relativePhotoRect.left ||
                    selectionRect.left > relativePhotoRect.right ||
                    selectionRect.bottom < relativePhotoRect.top ||
                    selectionRect.top > relativePhotoRect.bottom
                );
            });
        },

        endSelection() {
            this.isSelecting = false;
            window.removeEventListener("mousemove", this.updateSelection);
            window.removeEventListener("mouseup", this.endSelection);
        },

        select(photo: Photo, index: number, event: MouseEvent) {
            if (event.shiftKey && this.lastSelectedIndex !== null) {
                const range = [this.lastSelectedIndex, index].sort((a, b) => a - b);
                const photosToSelect = this.sortedPhotos.slice(range[0], range[1] + 1);

                photosToSelect.forEach((photoInRange) => {
                    if (!this.isSelectedPhoto(photoInRange)) {
                        this.selected.push(photoInRange);
                    }
                });
            } else {
                if (this.isSelectedPhoto(photo)) {
                    this.selected = this.selected.filter((item) => item.id !== photo.id);
                } else {
                    this.selected.push(photo);
                }
                this.lastSelectedIndex = index;
            }
        },

        isSelectedPhoto(photo: Photo): boolean {
            return !!this.selected.find((item) => item.id === photo.id);
        },

        deleteItem(photo: Photo) {
            if (this.isDeletingPhoto) return;

            if(this.isDeletingConfirmation === null) {
                this.isDeletingConfirmation = photo;
                return;
            }

            this.isDeletingPhoto = true;

            if (this.isTrashedFolder) {
                this.deleteTrashedPhoto({photo})
                    .then(() => {
                        this.isDeletingConfirmation = null;
                        this.isDeletingPhoto = false;
                        this.selected = [];
                        this.successAlert({message: this.$t('files.deleted_success')});
                    })
                    .catch((error) => {
                        console.error(error);
                        this.isDeletingConfirmation = null;
                        this.isDeletingPhoto = false;
                        this.selected = [];
                        this.errorAlert({message: this.$t('files.deleted_error')});
                    });

                return;
            }

            this.deletePhoto({photo})
                .then(() => {
                    this.isDeletingConfirmation = null;
                    this.isDeletingPhoto = false;
                    this.selected = [];
                    this.successAlert({message: this.$t('files.deleted_success')});
                })
                .catch(() => {
                    this.isDeletingConfirmation = null;
                    this.isDeletingPhoto = false;
                    this.selected = [];
                    this.errorAlert({message: this.$t('files.deleted_error')});
                });
        },

        recoverItem(photo: Photo) {
            if (this.isRecoveringDeletedItem) return;

            this.recoverDeletedPhoto({photo})
                .then(() => {
                    this.successAlert({message: this.$t('files.restored_success')});
                    this.selected = [];
                })
                .catch(({data}) => {
                    this.selected = [];

                    if(data?.message) {
                        this.errorAlert({
                            message: data.message,
                        });
                        return;
                    }

                    this.errorAlert({
                        message: this.$t('files.restored_error', { message: data?.message }),
                    });
                });
        },

        removeSelected(photos: Photo[] = []) {
            if (photos.length == 0) {
                this.selected = [];
            }
        },

        setCover(photo: Photo) {
            if (this.isDeletingPhoto) return;

            this.coverPhoto({photo})
                .then(() => {
                    this.successAlert({message: this.$t('files.cover_set_success')});

                    this.loadGallery({slug: this.gallery.slug})
                        .catch(() => {
                            this.errorAlert({
                                message: this.$t('files.cover_update_error'),
                            });
                        });
                })
                .catch(({data}) => {
                    this.selected = [];

                    if(data?.message) {
                        this.errorAlert({
                            message: data.message,
                        });
                        return;
                    }

                    this.errorAlert({
                        message: this.$t('files.cover_set_error', { message: data?.message }),
                    });
                });
        },

        onVideoMouseEnter(event: MouseEvent) {
            const video = (event.currentTarget as HTMLElement).querySelector('video');
            if (video) {
                video.play().catch(() => {
                    //
                });
            }
        },

        onVideoMouseLeave(event: MouseEvent) {
            const video = (event.currentTarget as HTMLElement).querySelector('video');
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
        },

        // Context menu methods
        showContextMenuForPhoto(photo: Photo, event: MouseEvent) {
            this.contextMenuPhoto = photo;
            this.contextMenuPosition = {
                x: event.clientX,
                y: event.clientY
            };
            this.showContextMenu = true;
        },

        closeContextMenu() {
            this.showContextMenu = false;
            this.contextMenuPhoto = null;
        },

        handleDocumentClick(event: MouseEvent) {
            // Закриваємо контекстне меню при кліку поза межами
            if (this.showContextMenu) {
                // Перевіряємо, чи клік не на фотографії (щоб не закрити меню одразу після відкриття)
                const target = event.target as HTMLElement;
                const isPhotoClick = target.closest('.photo-list-item');
                
                if (!isPhotoClick) {
                    this.closeContextMenu();
                }
            }
        },

        handleContextDelete(photo: Photo) {
            this.deleteItem(photo);
        },

        handleContextRecover(photo: Photo) {
            this.recoverItem(photo);
        },

        handleAddToScene({ photo, folder }: { photo: Photo; folder: any }) {
            this.handleSceneSelected({ photo, folder });
        },

        handleMakeCover(photo: Photo) {
            this.setCover(photo);
        },

        handleSceneSelected({ photo, folder }: { photo: Photo; folder: any }) {
            console.log('Adding photo to scene:', photo, 'Folder:', folder);
            this.moveToFolder({photos: [photo], folder: folder.slug})
                .then(() => {
                    this.successAlert({ 
                        message: this.$t('files.context_menu.add_to_scene') + ' - ' + folder.title 
                    });
                    // Оновлюємо список фото після переміщення
                    this.$emit('photo-moved');
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: this.$t('files.error.files', {
                            message: data?.message || 'Помилка при додаванні до сцени'
                        }),
                    });
                });
        },

        // Select all photos with CMD+A/CTRL+A
        selectAllPhotos() {
            this.selected = [...this.sortedPhotos];
        },

        deselectAllPhotos() {
            this.selected = [];
        },
    },

    mounted() {
        document.addEventListener("keydown", this.onKeyDown);
        document.addEventListener("keyup", this.onKeyUp);
        document.addEventListener("mousedown", this.startSelection);
        document.addEventListener("click", this.handleDocumentClick);
    },

    beforeUnmount() {
        document.removeEventListener("keydown", this.onKeyDown);
        document.removeEventListener("keyup", this.onKeyUp);
        document.removeEventListener("mousedown", this.startSelection);
        document.removeEventListener("click", this.handleDocumentClick);
    },
});
</script>

<style lang="scss" scoped>
.photos-list-container {
    position: relative;

    .gallery-photo-item {
        .card-img {
            display: block;
            width: 100%;
            height: 200px;
            max-width: 100%;
            max-height: 100%;
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
        }
    }

    .photo-list-item.col-2 {
        .gallery-photo-item {
            .card-img {
                height: 120px;
            }

            .gallery-photo-in-job-disabled {
                font-size: 12px;
            }
        }
    }

    .photo-list-item.col-4:nth-last-child(-n + 3) {
        .gallery-photo-item {
            margin-bottom: 0;
        }
    }

    .photo-list-item.col-3:nth-last-child(-n + 4) {
        .gallery-photo-item {
            margin-bottom: 0;
        }
    }

    .photo-list-item.col-2:nth-last-child(-n + 5) {
        .gallery-photo-item {
            margin-bottom: 0;
        }
    }

    .photo-list-item.col-6:nth-last-child(-n + 2) {
        .gallery-photo-item {
            margin-bottom: 0;
        }
    }
}

.selection-box {
    position: absolute;
    border: 2px dashed #3498db;
    background-color: rgba(52, 152, 219, 0.2);
    z-index: 100;
}

.is-selecting-container {
    cursor: crosshair;
}
</style>